import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLanguage,
  faUsers,
  faCodeBranch,
  faCamera,
  faMagic,
  faCloudUploadAlt,
  faArrowRight,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../images/logo.svg";
import { AuthContext } from "../context/AuthContext";

const FEATURES = [
  {
    icon: faLanguage,
    title: "Multi-language keys",
    description:
      "Organize every string in your product with a single source of truth and translate into any number of languages.",
  },
  {
    icon: faUsers,
    title: "Collaborative workflow",
    description:
      "Invite translators and reviewers, assign languages, and keep everyone working from the same copy.",
  },
  {
    icon: faCodeBranch,
    title: "Versioned releases",
    description:
      "Ship translations alongside your app with versioned keys so you always know what's in production.",
  },
  {
    icon: faCamera,
    title: "In-context screenshots",
    description:
      "Attach UI screenshots to each key so translators see exactly where a string appears.",
  },
  {
    icon: faMagic,
    title: "AI-powered drafts",
    description:
      "Kick off translations with DeepL suggestions, then let humans polish the final copy.",
  },
  {
    icon: faCloudUploadAlt,
    title: "OneSky & export",
    description:
      "Sync with OneSky or export straight to the formats your engineers already use.",
  },
];

const STATS = [
  { value: "40+", label: "Languages supported" },
  { value: "100%", label: "Translation coverage" },
  { value: "1-click", label: "AI drafts via DeepL" },
];

const Nav = ({ authState }) => (
  <nav className="sticky top-0 z-20 bg-white bg-opacity-90 border-b border-gray-100">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <img className="h-8" src={logo} alt="Relation" />
        <span className="text-lg font-extrabold tracking-tight text-gray-900">
          Relation
        </span>
      </Link>
      <div className="flex items-center gap-6">
        <a
          href="#features"
          className="hidden sm:inline text-gray-600 hover:text-gray-900 text-sm font-medium"
        >
          Features
        </a>
        <Link
          to="/signup"
          className="text-gray-600 hover:text-gray-900 text-sm font-medium"
        >
          Sign Up
        </Link>
        <Link
          to={authState.isAuthenticated ? "/projects" : "/login"}
          className="inline-flex items-center rounded-full px-5 py-2 bg-primary text-white text-sm font-semibold shadow-sm hover:shadow-md hover:opacity-95 transition"
        >
          {authState.isAuthenticated ? "Open app" : "Log in"}
        </Link>
      </div>
    </div>
  </nav>
);

const Hero = ({ authState }) => (
  <section className="relative overflow-hidden">
    <div
      aria-hidden
      className="absolute inset-0 bg-gradient opacity-40"
      style={{ zIndex: 0 }}
    />
    <div aria-hidden className="hero-blob hero-blob--teal" />
    <div aria-hidden className="hero-blob hero-blob--peach" />

    <div
      className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32"
      style={{ zIndex: 1 }}
    >
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-white bg-opacity-80 border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 mb-6">
          <span className="w-2 h-2 rounded-full bg-primary" />
          Translation management, simplified
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
          Ship your product in{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-primary">every language</span>
            <span
              aria-hidden
              className="absolute left-0 right-0 bottom-1 h-3 bg-primary rounded"
              style={{ opacity: 0.2, zIndex: 0 }}
            />
          </span>
          .
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed">
          Relation is the translation workspace for product teams. Manage keys,
          collaborate with translators, and ship localized releases without the
          spreadsheet chaos.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to={authState.isAuthenticated ? "/projects" : "/signup"}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white text-base font-semibold px-7 py-3 shadow-lg hover:shadow-xl hover:opacity-95 transition"
          >
            Get started
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
          <a
            href="#features"
            className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-800 text-base font-semibold px-7 py-3 hover:border-gray-400 transition"
          >
            See features
          </a>
        </div>

        <dl className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl bg-white bg-opacity-80 border border-gray-200 px-5 py-4"
            >
              <dt className="text-2xl font-bold text-gray-900">{stat.value}</dt>
              <dd className="text-sm text-gray-600 mt-1">{stat.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-2xl">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
          Everything you need to localize
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          From the first string to the final release, Relation keeps engineers,
          translators, and reviewers in sync.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((feature) => (
          <div
            key={feature.title}
            className="group rounded-2xl border border-gray-200 bg-white p-7 hover:border-primary hover:shadow-lg transition"
          >
            <div className="w-12 h-12 rounded-xl bg-primary bg-opacity-10 text-primary flex items-center justify-center text-xl group-hover:bg-opacity-100 group-hover:text-white transition">
              <FontAwesomeIcon icon={feature.icon} />
            </div>
            <h3 className="mt-5 text-lg font-semibold text-gray-900">
              {feature.title}
            </h3>
            <p className="mt-2 text-gray-600 leading-relaxed">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Workflow = () => {
  const steps = [
    "Create a project and pick a base language",
    "Invite translators and reviewers per language",
    "Add keys, screenshots, and context",
    "Review, verify, and export to production",
  ];
  return (
    <section className="bg-gray-50 py-24 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            A workflow your team will actually use
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Replace the patchwork of spreadsheets, shared docs, and chat threads
            with one place where every string has an owner and a status.
          </p>
          <ul className="mt-8 space-y-4">
            {steps.map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-gray-800 pt-0.5">{step}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative">
          <div
            className="absolute inset-0 bg-gradient opacity-30 rounded-3xl"
            style={{ filter: "blur(40px)" }}
          />
          <div className="relative rounded-2xl bg-white border border-gray-200 shadow-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-yellow-400" />
              <span className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-3 text-xs text-gray-500 font-mono">
                project / checkout
              </span>
            </div>
            <div className="p-6 space-y-3 font-mono text-sm">
              {[
                { key: "checkout.title", en: "Checkout", status: "done" },
                { key: "checkout.cta", en: "Place order", status: "done" },
                {
                  key: "checkout.subtitle",
                  en: "Almost there!",
                  status: "review",
                },
                { key: "checkout.error", en: "Payment failed", status: "todo" },
              ].map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 border border-gray-100"
                >
                  <div className="min-w-0">
                    <div className="text-gray-500 text-xs truncate">
                      {row.key}
                    </div>
                    <div className="text-gray-900 truncate">{row.en}</div>
                  </div>
                  <StatusPill status={row.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    done: { label: "Verified", cls: "bg-green-100 text-green-800" },
    review: { label: "In review", cls: "bg-yellow-100 text-yellow-800" },
    todo: { label: "To translate", cls: "bg-gray-100 text-gray-700" },
  };
  const s = map[status];
  return (
    <span
      className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}
    >
      {status === "done" && <FontAwesomeIcon icon={faCheckCircle} />}
      {s.label}
    </span>
  );
};

const CTA = ({ authState }) => (
  <section className="py-24">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <div className="relative overflow-hidden rounded-3xl bg-gray-900 px-8 py-16 sm:px-16 sm:py-20 text-center">
        <div aria-hidden className="absolute inset-0 bg-gradient opacity-20" />
        <div
          aria-hidden
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-primary"
          style={{ opacity: 0.3, filter: "blur(60px)" }}
        />
        <div className="relative">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Nothing gets lost in translation.
          </h2>
          <p className="mt-4 text-lg text-gray-300 max-w-xl mx-auto">
            Bring your translation workflow into one modern workspace. Free to
            start.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={authState.isAuthenticated ? "/projects" : "/signup"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white text-base font-semibold px-7 py-3 shadow-lg hover:opacity-95 transition"
            >
              {authState.isAuthenticated
                ? "Go to projects"
                : "Create free account"}
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-full border border-gray-700 bg-gray-800 text-white text-base font-semibold px-7 py-3 hover:bg-gray-700 transition"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-gray-100 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src={logo} alt="Relation" className="h-6" />
        <span className="text-sm text-gray-500">
          © {new Date().getFullYear()} Relation. All rights reserved.
        </span>
      </div>
      <div className="flex items-center gap-6 text-sm text-gray-500">
        <a href="#features" className="hover:text-gray-900">
          Features
        </a>
        <Link to="/login" className="hover:text-gray-900">
          Log in
        </Link>
        <Link to="/signup" className="hover:text-gray-900">
          Sign up
        </Link>
      </div>
    </div>
  </footer>
);

const Home = () => {
  const { authState } = useContext(AuthContext);
  return (
    <div className="flex-1 overflow-y-auto bg-white text-gray-900">
      <Nav authState={authState} />
      <Hero authState={authState} />
      <Features />
      <Workflow />
      <CTA authState={authState} />
      <Footer />
    </div>
  );
};

export default Home;
